// ============================================================================
// BOOK · every colour slot shows the best photo the system has (ORD-BOOK-1e)
// ============================================================================
// bookSources picks a card's colours and fills them from steps 1–3 (studio ·
// not _s · sample). What is still empty is filled here, in this order:
//   4 option   a "More option" / MO photo that carries THAT colour        OPTION
//   5 loose    a library photo of the model with no colour number, in name
//              order, one per slot                                       COLOUR?
//   6 devimg   a development card image not shown elsewhere on the card  DEV PHOTO
//   7 sample   a sheet colourway of the development not shown elsewhere  SAMPLE
// A slot stays empty only when the system has nothing at all for the model — or when it is a PI colour
// with no photo of its own (CLB-104 25.10: slot.pi): it takes a pin, never another colour's photo.
// Never the same photo twice on one card. A pin (colours[key].pin) wins over
// steps 2–7; a studio side photo wins over the pin.
// The colour of a name is read here, tolerantly, for the BOOK only — the Files
// parser is unchanged — and only right after one of the card's own codes, so a
// timestamp such as "(10-22-09-52-25)" is never read as a colour.
// ============================================================================

import { emailPictures } from './bookRnc';

const esc = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const stem = (name) => String(name || '').replace(/\.[a-z0-9]{2,5}$/i, '');

/**
 * A name that starts with "canceled" (any case, with or without a space: "canceled26080040  CL9388-10…")
 * is a canceled photo, whatever its summary says — never used at any step (ORD-BOOK-1f).
 */
export const isCanceledName = (name) => /^\s*cancel+ed/i.test(String(name || ''));

/**
 * "D260520 DS1926A -2.jpg" → 2 · "30003-2=CL2700234 DS3363-2-1.png" → 2 · "DS2568A.JPG" → null (codes = the card's codes).
 * ORD-BOOK-1f: full-width brackets read as a space ("CL3860-5（m.blk ）" → 5), and the item's own digits
 * glued to an 8-digit PI number ("3537-926080060 CL3537-MO-5" → 9: digits, dash, 1–2 digits, then "=",
 * a space, the end, or exactly 8 more digits).
 */
export function colourOfName(name, codes) {
  if (isCanceledName(name)) return null;
  const base = stem(name).replace(/[（）]/g, ' ');
  for (const code of codes || []) {
    const c = String(code || '').trim();
    if (!c) continue;
    const tokens = [c];
    const bare = c.match(/^CL(\d{4,})$/i);                // the folder-number form: "30003-2=…"
    if (bare) tokens.push(bare[1]);
    for (const t of tokens) {
      const m = base.match(new RegExp(`(?:^|[^A-Z0-9])${esc(t)}\\s*-\\s*0*(\\d{1,3})(?![0-9])`, 'i'));
      if (m && Number(m[1]) > 0) return Number(m[1]);
    }
    if (bare) {
      const g = base.match(new RegExp(`(?:^|[^0-9])${bare[1]}-(\\d{1,2})(?==|\\s|$|\\d{8}(?!\\d))`));
      if (g && Number(g[1]) > 0) return Number(g[1]);
    }
  }
  return null;
}

export const refOf = (ph) => (ph && ph.p ? `p:${ph.p}` : ph && ph.url ? `u:${ph.url}` : null);
const nameOf = (ph) => String((ph && (ph.name || ph.p || ph.url)) || '');

/**
 * Every photo the system has for a card, for the chooser and the fill: [{ ref, n, source, photo }].
 * src: { side, other, option: {n: [ph]}, loose: [ph], sample: [{n, p, frame}], devAll: [url] }.
 */
export function candidatesOf(src) {
  const out = [], seen = new Set();
  const add = (photo, source, n) => {
    const ref = refOf(photo);
    if (!ref || seen.has(ref)) return;
    seen.add(ref);
    out.push({ ref, n: n == null ? null : Number(n), source, photo: { ...photo, source } });
  };
  const byN = (o) => Object.keys(o || {}).map(Number).sort((a, b) => a - b);
  for (const n of byN(src.side)) add(src.side[n], 'studio', n);
  for (const n of byN(src.other)) add(src.other[n], 'nots', n);
  for (const s of src.sample || []) if (s.p) add({ p: s.p, frame: s.frame || null }, 'sample', s.n);
  for (const n of byN(src.option)) for (const ph of src.option[n]) add(ph, 'option', n);
  for (const ph of src.loose || []) add(ph, 'loose', null);
  for (const url of src.devAll || []) add({ url }, 'devimg', null);
  for (const e of src.emails || []) if (e && e.image_url) add({ url: e.image_url, name: e.desc || '' }, 'email', null);
  return out;
}

/** A pinned candidate for slot key, or null (the photo may be gone since — then the rule decides). */
export function pinOf(choice, key, cands) {
  const ref = choice && choice.pin && choice.pin[key];
  return ref ? cands.find((c) => c.ref === ref) || null : null;
}

/** The choice with slot `key` pinned to `ref` (null = Reset); every other key of the choice is kept. */
export function withPin(choice, key, ref) {
  const pin = { ...((choice && choice.pin) || {}) };
  if (ref) pin[key] = ref; else delete pin[key];
  const next = { ...(choice || {}) };
  if (Object.keys(pin).length) next.pin = pin; else delete next.pin;
  return next;
}

/**
 * Fill a card's slots: pins first (never over a studio photo), then steps 4–7 for every slot still
 * empty. Slots come from bookSources (steps 1–3 already applied). A new-colour (RNC) slot with its number
 * (ORD-BOOK-1f) takes a pin, an option of that colour, then the decision email's picture — never the
 * pool of steps 5–7; an RNC slot with no number stays an upload slot.
 */
export function fillSlots(slots, src, choice) {
  const cands = candidatesOf(src);
  const used = new Set(slots.filter((s) => s.kind === 'photo').map((s) => refOf(s.photo)).filter(Boolean));
  const asPhoto = (s, c, extra = {}) => ({ ...s, kind: 'photo', photo: c.photo, source: c.source, ...extra });
  const out = slots.map((s) => {
    if (s.rnc && s.n == null) return s;
    const pin = pinOf(choice, s.key, cands);
    if (!pin || (s.kind === 'photo' && s.source === 'studio')) return s;
    if (s.kind === 'photo') used.delete(refOf(s.photo));
    used.add(pin.ref);
    return asPhoto(s, pin, { pinned: true });
  });
  const free = (c) => !used.has(c.ref);
  const take = (c) => { used.add(c.ref); return c; };
  for (let i = 0; i < out.length; i += 1) {                        // 4 · an option of that colour
    const s = out[i];
    if (s.kind === 'photo' || s.n == null || s.pi) continue;     // a PI colour: its own photo or nothing (CLB-104)
    const c = cands.find((x) => x.source === 'option' && x.n === s.n && free(x));
    if (c) out[i] = asPhoto(s, take(c));
  }
  // 5 (RNC only) · the decision email's picture of that new colour (bookRnc.emailPictures)
  const rncNs = out.filter((s) => s.rnc && s.n != null).map((s) => s.n);
  if (rncNs.length && (src.emails || []).length) {
    const pics = emailPictures(src.emails, rncNs, src.rncWords || {});
    for (let i = 0; i < out.length; i += 1) {
      const s = out[i], e = s.rnc && s.n != null && s.kind !== 'photo' ? pics[s.n] : null;
      if (!e) continue;
      const c = { ref: refOf(e.photo), photo: { ...e.photo, source: e.source }, source: e.source };
      if (free(c)) out[i] = asPhoto(s, take(c));
    }
  }
  const pool = [
    ...cands.filter((x) => x.source === 'loose').sort((a, b) => nameOf(a.photo).localeCompare(nameOf(b.photo), undefined, { numeric: true })),
    ...cands.filter((x) => x.source === 'devimg'),
    ...cands.filter((x) => x.source === 'sample'),
  ];
  for (let i = 0; i < out.length; i += 1) {                        // 5 · 6 · 7 · in that order, one photo per slot
    const s = out[i];
    if (s.rnc || s.kind === 'photo' || s.pi) continue;
    const c = pool.find(free);
    if (!c) break;
    out[i] = asPhoto(s, take(c));
  }
  return out;
}

export const UNCONFIRMED = new Set(['option', 'loose', 'emailq']);
